<template>
  <dx-modal :value="show" :title="editing ? 'Editar glosa manual' : 'Registrar glosa'" size="md"
            :closable="!saving" @input="close()" id="modal-manual-gloss">
    <div class="billing-gloss manual-gloss">
      <div class="bg-eyebrow mb-1">{{ eyebrow }}</div>
      <div class="stenci-group stenci-alert bg-help" v-if="!editing">
        <p>
          Use quando a operadora informou a glosa por portal, e-mail ou carta. Se você tem o XML do
          demonstrativo, prefira <b>importá-lo</b>: a conciliação é automática.
        </p>
      </div>

      <div class="form-group">
        <label class="form-label">Nível</label>
        <div class="btn-group bg-seg">
          <button class="btn btn-sm" v-for="option in levels" :key="option.value"
                  :class="form.level === option.value ? 'btn-secondary' : 'btn-gray'"
                  :disabled="editing" @click="setLevel(option.value)">{{ option.label }}</button>
        </div>
      </div>

      <div class="columns">
        <div class="column col-6 col-sm-12 form-group" v-if="form.level !== 'protocol'"
             :class="{'has-error': $v.form.accountId.$error}">
          <label class="form-label">Guia</label>
          <select class="form-select" v-model="form.accountId" :disabled="editing"
                  @change="onGuideChange" @blur="$v.form.accountId.$touch()">
            <option value="">[Selecione a guia]</option>
            <option v-for="guide in guides" :value="String(guide.accountId)"
                    :key="String(guide.accountId)">
              {{ guideOption(guide) }}
            </option>
          </select>
          <div class="form-input-hint" v-if="$v.form.accountId.$error">Campo obrigatório</div>
        </div>
        <div class="column col-6 col-sm-12 form-group" v-if="form.level === 'item'"
             :class="{'has-error': $v.form.itemId.$error}">
          <label class="form-label">Item</label>
          <select class="form-select" v-model="form.itemId" :disabled="editing || !form.accountId"
                  @blur="$v.form.itemId.$touch()">
            <option value="">[Selecione o item]</option>
            <option v-for="item in guideItems" :value="String(item.itemId)"
                    :key="String(item.itemId)">
              {{ item.sequencialItem }} · {{ item.expense && item.expense.code }}
              {{ item.expense && item.expense.name }}
              · {{ item.values.informed | currency }}
            </option>
          </select>
          <div class="form-input-hint" v-if="$v.form.itemId.$error">Campo obrigatório</div>
        </div>
      </div>

      <div class="form-group" :class="{'has-error': $v.form.code.$error}">
        <label class="form-label">
          Código da glosa (Tabela 38)
          <button class="btn btn-link btn-sm bg-edit-description" v-if="!editingDescription"
                  @click="editDescription">Editar descrição</button>
        </label>
        <gloss-code-autocomplete id="manual-gloss-code" v-model="form.code"
                                 :insurance-id="insuranceId"
                                 @select="onCodeSelect" @blur="$v.form.code.$touch()"/>
        <div class="form-input-hint" v-if="$v.form.code.$error">Campo obrigatório</div>
      </div>
      <div class="form-group" v-if="editingDescription"
           :class="{'has-error': $v.form.description.$error}">
        <label class="form-label">Descrição</label>
        <input type="text" class="form-input" ref="description" v-model="form.description"
               maxlength="500" placeholder="Texto que a operadora informou"
               @blur="$v.form.description.$touch()">
        <div class="form-input-hint" v-if="$v.form.description.$error">Campo obrigatório</div>
        <div class="form-input-hint" v-else>
          Use quando a operadora descreveu a glosa com outras palavras
        </div>
      </div>

      <div class="columns">
        <div class="column col-4 col-sm-12 form-group" :class="{'has-error': $v.form.value.$error}">
          <label class="form-label">Valor glosado</label>
          <div class="input-group">
            <span class="input-group-addon addon-sm">R$</span>
            <dx-input-number id="manual-gloss-value" v-model="form.value"
                             class="form-input text-right"
                             :precision="2" maxlength="10" @blur="$v.form.value.$touch()"/>
          </div>
          <template v-if="$v.form.value.$error">
            <div class="form-input-hint" v-if="!$v.form.value.minValue || !$v.form.value.required">
              Informe um valor
            </div>
            <div class="form-input-hint" v-else-if="!$v.form.value.maxValue">
              Maior que o saldo
              {{ form.level === 'item'
                ? 'do item'
                : (form.level === 'guide'
                ? 'da guia'
                : 'do protocolo') }}
              ({{ maxValue | currency }})
            </div>
          </template>
          <button class="btn btn-link btn-sm bg-all" v-if="!editing && maxValue > 0 && form.value
          !== maxValue"
                  @click="form.value = maxValue">Glosar tudo ({{ maxValue | currency }})</button>
        </div>
        <div class="column col-4 col-sm-12 form-group">
          <label class="form-label">
            {{ form.level === 'item' ? 'Informado no item' : 'Saldo disponível' }}
          </label>
          <input type="text" class="form-input text-right" readonly
                 :value="$options.filters.currency(informedValue)">
        </div>
        <div class="column col-4 col-sm-12 form-group"
             :class="{'has-error': $v.form.occurredAt.$error}">
          <label class="form-label">Data da glosa</label>
          <dx-input-date id="manual-gloss-date" class="form-input" v-model="form.occurredAt"
                         @blur="$v.form.occurredAt.$touch()"/>
          <template v-if="$v.form.occurredAt.$error">
            <div class="form-input-hint" v-if="!$v.form.occurredAt.required">Campo obrigatório</div>
            <div class="form-input-hint" v-else-if="!$v.form.occurredAt.maxDate">
              Não pode ser futura
            </div>
            <div class="form-input-hint" v-else>Data inválida</div>
          </template>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">Observação <span class="text-gray">(opcional)</span></label>
        <textarea class="form-input" rows="2" v-model="form.notes" maxlength="1000"
                  placeholder="Ex.: informado por e-mail da auditoria em 14/09"></textarea>
      </div>

      <div class="cr-panel bg-split" v-if="form.level !== 'item' && splitRows.length > 0">
        <div class="cr-panel-head">
          <div class="cr-panel-title cr-panel-title-sm">Rateio por item</div>
          <div class="cr-panel-subtitle">
            Proporcional ao valor informado de cada item; cada linha vira uma glosa própria.
          </div>
        </div>
        <div class="cr-table-scroll">
          <table class="table">
            <thead>
            <tr>
              <th>Seq</th>
              <th>Item</th>
              <th class="text-right">Informado</th>
              <th class="text-right">%</th>
              <th class="text-right">Rateado</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in splitRows" :key="String(row.item.itemId)">
              <td><span class="bg-seq">{{ row.item.sequencialItem }}</span></td>
              <td class="bg-proc">
                <code>
                  {{ row.item.expense && row.item.expense.code }}</code><b>
                  {{ row.item.expense && row.item.expense.name }}
                </b>
                <small v-if="form.level === 'protocol'">{{ row.guideName }}</small>
              </td>
              <td class="text-right tnum">{{ row.item.values.informed | currency }}</td>
              <td class="text-right tnum text-gray">{{ row.percent }}%</td>
              <td class="text-right tnum text-red">−{{ row.share | currency }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="cr-box bg-impact-box">
        <div class="bg-summary-h">Impacto ao registrar</div>
        <div class="bg-summary-k" v-if="receivable">
          <span>
            Título {{ receivable.document }}
            <span class="text-xs text-gray">
              · {{ receivable.status === 'paid' ? 'já baixado' : 'aberto' }}
            </span>
          </span>
          <span class="bg-summary-v" v-if="receivable.status !== 'paid'">
            {{ receivable.value.total | currency }}
            <fa-icon :icon="['fal', 'arrow-right']" class="text-gray"/>
            <span class="text-red">{{ receivableAfter | currency }}</span>
            <span class="text-xs text-gray">(−{{ effectiveValue | currency }})</span>
          </span>
          <span class="bg-summary-v text-amber" v-else>sem alteração</span>
        </div>
        <div class="bg-summary-k" v-else>
          <span>Título do lote</span>
          <span class="bg-summary-v text-gray">não encontrado</span>
        </div>
        <div class="bg-summary-k" v-for="line in transferLines" :key="line.key"
             :class="{ 'bg-summary-paid': line.paid }">
          <span>
            Repasse {{ line.name }}
            <span class="text-xs" :class="line.paid ? 'text-amber' : 'text-gray'">
              · {{ line.paid ? 'já pago' : 'aberto' }}
            </span>
          </span>
          <span class="bg-summary-v text-red">−{{ line.effect | currency }}</span>
        </div>
        <div class="text-xs text-amber" v-if="transferLines.some((line) => line.paid)">
          Repasse já pago: a diferença vira ajuste no próximo repasse do profissional.
        </div>
        <div class="text-xs text-amber" v-if="receivable && receivable.status === 'paid'">
          Título já baixado: a glosa é registrada sem alterar o financeiro.
        </div>
        <div class="text-xs text-green mt-1" v-if="!editing">
          A glosa nasce <b>Aberta</b> e pode ser recursada ou aceita na aba Glosas.
        </div>
      </div>
    </div>

    <template slot="footer">
      <button class="btn btn-primary btn-icon btn-icon-left mr-1" :class="{ loading: saving }"
              :disabled="saving" @click="save">
        <fa-icon :icon="['fal', 'check']"/>{{ editing ? 'Salvar glosa' : 'Registrar glosa' }}
      </button>
      <button class="btn" :disabled="saving" @click="close()">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment';
import {
  required, requiredIf, minValue, maxValue,
} from 'vuelidate/lib/validators';
import { date, maxDate } from '@/data/validators';
import GlossCodeAutocomplete from '../components/GlossCodeAutocomplete.vue';
import {
  guideTypes, guideLabel, idOf, num, round2, splitProportional, transferEffect,
} from '../helpers';

function codeDescription(gloss) {
  return gloss && gloss.codeDescription ? gloss.codeDescription : null;
}

const LEVELS = [
  { value: 'item', label: 'Item' },
  { value: 'guide', label: 'Guia inteira' },
  { value: 'protocol', label: 'Protocolo' },
];

function itemBalance(item) {
  const balance = num(item.values.informed) - num(item.values.glossed) + num(item.values.recovered);
  return round2(Math.max(balance, 0));
}

export default {
  components: {
    GlossCodeAutocomplete,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    batchId: {
      type: String,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    financial: {
      type: Object,
      default: null,
    },
    accountId: {
      type: String,
      default: null,
    },
    itemId: {
      type: String,
      default: null,
    },
    gloss: {
      type: Object,
      default: null,
    },
  },
  data() {
    const gloss = this.gloss || null;
    return {
      levels: LEVELS,
      saving: false,
      editingDescription: !!(gloss && gloss.description
        && gloss.description !== codeDescription(gloss)),
      form: {
        level: gloss ? gloss.level : 'item',
        accountId: gloss ? String(gloss.accountId) : (this.accountId || ''),
        itemId: gloss ? String(gloss.itemId || '') : (this.itemId || ''),
        code: gloss ? { code: gloss.code, description: gloss.description } : null,
        description: gloss ? gloss.description : '',
        value: gloss ? num(gloss.value) : 0,
        occurredAt: gloss ? moment(gloss.occurredAt).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        notes: gloss ? (gloss.notes || '') : '',
      },
    };
  },
  computed: {
    editing() {
      return !!this.gloss;
    },
    batch() {
      return this.view.batch;
    },
    insuranceId() {
      return this.batch ? idOf(this.batch.insurance) : null;
    },
    eyebrow() {
      const insurance = this.batch && this.batch.insurance;
      return [
        `Lote ${this.batch.number}`,
        insurance && insurance.name,
        guideTypes[this.batch.guideType] || this.batch.guideType,
      ].filter(Boolean).join(' · ');
    },
    guides() {
      return this.view.guides || [];
    },
    guide() {
      return this.guides.find(guide => String(guide.accountId) === this.form.accountId) || null;
    },
    guideItems() {
      return this.guide ? this.guide.items : [];
    },
    item() {
      return this.guideItems.find(item => String(item.itemId) === this.form.itemId) || null;
    },
    targetItems() {
      if (this.form.level === 'item') return this.item ? [this.item] : [];
      if (this.form.level === 'guide') return this.guideItems;
      return this.guides.flatMap(guide => guide.items);
    },
    maxValue() {
      const balances = this.targetItems.map(itemBalance);
      const total = round2(balances.reduce((sum, value) => sum + value, 0));
      if (this.editing) return round2(total + num(this.gloss.value));
      return total;
    },
    informedValue() {
      if (this.form.level === 'item') return this.item ? num(this.item.values.informed) : 0;
      return this.maxValue;
    },
    effectiveValue() {
      return round2(Math.min(num(this.form.value), this.maxValue));
    },
    shares() {
      if (this.form.level === 'item') return this.targetItems.map(() => this.effectiveValue);
      const weights = this.targetItems.map(item => num(item.values.informed));
      return splitProportional(this.effectiveValue, weights);
    },
    splitRows() {
      const total = this.targetItems.reduce((sum, item) => sum + num(item.values.informed), 0);
      return this.targetItems.map((item, index) => {
        const guide = this.guides.find(entry => entry.items.includes(item));
        return {
          item,
          share: this.shares[index] || 0,
          percent: total > 0 ? String(round2((num(item.values.informed) / total) * 100)).replace('.', ',') : '0',
          guideName: guide ? `${guideLabel(guide)} · ${guide.patient && guide.patient.name}` : '',
        };
      });
    },
    receivable() {
      return (this.financial && this.financial.receivable) || null;
    },
    receivableAfter() {
      if (!this.receivable) return 0;
      const delta = this.editing
        ? round2(this.effectiveValue - num(this.gloss.value))
        : this.effectiveValue;
      return round2(Math.max(num(this.receivable.value.total) - delta, 0));
    },
    transferLines() {
      const transfers = (this.financial && this.financial.transfers) || [];
      const bundles = new Map();
      this.targetItems.forEach((item, index) => {
        const share = this.shares[index] || 0;
        if (share <= 0 || !item.transfer || !num(item.transfer.amount)) return;
        const effect = transferEffect({
          amount: item.transfer.amount,
          glossedAmount: item.transfer.glossedAmount,
          recoveredAmount: item.transfer.recoveredAmount,
          insurance: item.values.informed,
          value: share,
        });
        if (effect <= 0) return;
        const bundleId = item.transfer.financialBundleId
          ? String(item.transfer.financialBundleId)
          : null;
        const financial = bundleId
          ? transfers.find(doc => String(doc.bundleId) === bundleId) || null
          : null;
        const key = bundleId || `professional:${idOf(item.professional)}`;
        const current = bundles.get(key) || {
          key,
          name: (financial && financial.entity && financial.entity.name)
            || (item.professional && item.professional.name) || 'Profissional',
          paid: financial ? financial.status === 'paid' : false,
          effect: 0,
        };
        current.effect = round2(current.effect + effect);
        bundles.set(key, current);
      });
      return [...bundles.values()];
    },
  },
  validations() {
    return {
      form: {
        level: { required },
        accountId: { required: requiredIf(() => this.form.level !== 'protocol') },
        itemId: { required: requiredIf(() => this.form.level === 'item') },
        code: { required },
        description: { required },
        value: { required, minValue: minValue(0.01), maxValue: maxValue(this.maxValue) },
        occurredAt: { required, date, maxDate: maxDate(moment().format('YYYY-MM-DD')) },
      },
    };
  },
  mounted() {
    if (!this.editing && this.accountId && !this.itemId) {
      this.form.level = 'guide';
    }
  },
  methods: {
    setLevel(level) {
      this.form.level = level;
      if (level === 'protocol') {
        this.form.accountId = '';
        this.form.itemId = '';
      } else if (level === 'guide') {
        this.form.itemId = '';
      }
      this.$v.form.$reset();
    },
    onGuideChange() {
      this.form.itemId = '';
    },
    guideOption(guide) {
      const parts = [guideLabel(guide).replace('Guia ', '')];
      if (guide.patient && guide.patient.name) parts.push(guide.patient.name);
      parts.push(this.$options.filters.currency(guide.totals.informed));
      return parts.join(' · ');
    },
    editDescription() {
      this.editingDescription = true;
      this.$nextTick(() => {
        if (this.$refs.description) this.$refs.description.focus();
      });
    },
    onCodeSelect(item) {
      if (item) this.form.description = item.description;
    },
    save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return Promise.resolve();

      this.saving = true;
      const payload = {
        code: this.form.code.code,
        description: this.form.description.trim(),
        value: round2(this.form.value),
        occurredAt: this.form.occurredAt,
        notes: this.form.notes.trim() || null,
      };
      const request = this.editing
        ? this.$http.put(`/billing-glosses/${idOf(this.gloss)}`, payload)
        : this.$http.post('/billing-glosses', {
          ...payload,
          batchId: this.batchId,
          level: this.form.level,
          accountId: this.form.level === 'protocol' ? null : this.form.accountId,
          itemId: this.form.level === 'item' ? this.form.itemId : null,
        });

      return request
        .then(() => {
          this.$toast.success(this.successMessage());
          this.close(true);
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    successMessage() {
      const { code } = this.form.code;
      if (this.editing) return `Glosa ${code} atualizada.`;
      const guideName = this.guide ? guideLabel(this.guide).toLowerCase() : 'guia';
      if (this.form.level === 'item') {
        return `Glosa ${code} registrada no item ${this.item ? this.item.sequencialItem : ''} da ${guideName}.`;
      }
      const count = this.targetItems.length;
      if (this.form.level === 'guide') {
        return `Glosa ${code} registrada na ${guideName} (rateada em ${count} ${count === 1 ? 'item' : 'itens'}).`;
      }
      return `Glosa ${code} registrada no protocolo (rateada em ${count} ${count === 1 ? 'item' : 'itens'}).`;
    },
    close(saved = false) {
      this.$emit('close', saved);
    },
  },
};
</script>

<style lang="scss">
@import "../gloss.scss";

#modal-manual-gloss {
  .manual-gloss {
    .bg-all { padding: 0; }
    .bg-split { margin-top: 0; margin-bottom: $layout-spacing; }
    .bg-impact-box { margin-bottom: 0; }
  }
}
</style>
